/**
 * Qué le decimos al dueño sobre su facturación en el Dashboard (7-oct-2026).
 *
 * El hecho que lo motivó: una organización CANCELADA entraba al panel con la etiqueta «STARTER»
 * y sin ningún aviso, mientras el servidor ya bloqueaba el bot y los envíos. La etiqueta salía
 * de `org.planId` (que el webhook deja en 'starter' al cancelar) y nadie miraba `billing.status`.
 *
 * LA DECISIÓN SALE DEL MISMO VOCABULARIO QUE EL SERVIDOR: `STATUS_MAP` es copia letra por letra
 * del de `functions/utils/paymentAccess.cjs` (src/ no puede importar de functions/); la paridad la
 * sostiene `functions/__tests__/estadoDeFacturacion.jest.test.js`, que recorre TODOS los valores
 * de aquél y exige el mismo canónico. La PUERTA del servidor no cambia: esto sólo informa.
 *
 *   canceled   → «Cancelado» + banner con enlace a /contratar
 *   canceling  → «Activo hasta el <currentPeriodEnd>» (cancel_at_period_end: pagó ese período)
 *   past_due   → el banner de dunning (acceso conservado; hay que actualizar el pago)
 *   el resto   → la etiqueta del plan, sin aviso
 *
 * CommonJS y pura para probarla desde `functions/__tests__` (los tests de `src/` no corren).
 */
const STATUS_MAP = {
  active: 'active',
  activo: 'active',
  vencido: 'past_due',
  past_due: 'past_due',
  cancelando: 'canceling',
  canceling: 'canceling',
  trial: 'trialing',
  prueba: 'trialing',
  trialing: 'trialing',
  canceled: 'canceled',
  cancelado: 'canceled',
  impago: 'unpaid',
  unpaid: 'unpaid',
  reembolsado: 'refunded',
  refunded: 'refunded',
  fallido: 'payment_failed',
  payment_failed: 'payment_failed',
  incompleto: 'incomplete',
  incomplete: 'incomplete',
  expirado: 'incomplete_expired',
  incomplete_expired: 'incomplete_expired',
  pausado: 'paused',
  paused: 'paused',
  pendiente_pago: 'pending_payment',
  pending_payment: 'pending_payment',
};

function canonico(raw) {
  if (raw === null || raw === undefined || raw === '') return 'missing';
  return STATUS_MAP[String(raw).toLowerCase().trim()] || 'unknown';
}

/** `currentPeriodEnd` llega como Timestamp de Firestore, Date, número (ms o s) o ISO. */
function fechaDe(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') return new Date(v < 1e12 ? v * 1000 : v);
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {object|null} billing  `organization.billing`
 * @returns {{canonico: string, aviso: 'cancelado'|'cancelando'|'dunning'|null, hasta: Date|null}}
 */
function avisoDeFacturacion(billing) {
  const c = canonico(billing && billing.status);
  if (c === 'canceled') return { canonico: c, aviso: 'cancelado', hasta: null };
  if (c === 'canceling') return { canonico: c, aviso: 'cancelando', hasta: fechaDe(billing && billing.currentPeriodEnd) };
  if (c === 'past_due') return { canonico: c, aviso: 'dunning', hasta: null };
  return { canonico: c, aviso: null, hasta: null };
}

/**
 * La etiqueta de la cabecera: `cancelado` cuando la facturación está cancelada; si no, el plan.
 * @returns {{clase: 'cancelado'|string, esCancelado: boolean}}
 */
function etiquetaDelPlan({ plan, billing }) {
  const { aviso } = avisoDeFacturacion(billing);
  if (aviso === 'cancelado') return { clase: 'cancelado', esCancelado: true };
  return { clase: String(plan || 'starter').toLowerCase(), esCancelado: false };
}

/**
 * Cuenta interna de PUX (7-oct-2026): `billing.interna === true`, puesto a mano, sin suscripción
 * ni cliente en Stripe. Tiene acceso (status `active`) y no se le cobra; Facturación lo dice y no
 * ofrece cambiar ni cancelar el plan, porque no hay nada en Stripe que cambiar.
 */
function esCuentaInterna(billing) {
  return !!(billing && billing.interna === true);
}

module.exports = { avisoDeFacturacion, etiquetaDelPlan, esCuentaInterna, canonico, fechaDe, STATUS_MAP };
